import type {
  CompiledTextElement,
  LayoutAdjustmentDiagnostic,
  TextFontStyle,
} from "../types/template";
import { createCharMeasure } from "./fontWidthData";
import {
  charFillLines,
  charFillWithEllipsis,
  ellipsizeLines,
  wrapTextWithExplicitBreaks,
} from "./textWrap";

interface TextLayoutRequest {
  element: CompiledTextElement;
  value: string;
  widthMm: number;
  heightMm?: number;
  maxScale: number;
}

export interface ResolvedTextLayout {
  lines: string[];
  lineCount: number;
  font: TextFontStyle;
  contentHeightMm: number;
  diagnostic?: LayoutAdjustmentDiagnostic;
  overflowed: boolean;
  ellipsized: boolean;
}

function buildScaledFont(
  base: CompiledTextElement["font"],
  scale: number,
): TextFontStyle {
  return {
    family: base.family,
    heightMm: base.heightMm * scale,
    widthMm: base.widthMm * scale,
    lineSpacingMm: base.lineSpacingMm * scale,
    maxLines: base.maxLines,
    align: base.align,
  };
}

function heightFromLines(lineCount: number, font: TextFontStyle): number {
  return (
    lineCount * font.heightMm + Math.max(0, lineCount - 1) * font.lineSpacingMm
  );
}

function maxLinesByHeight(heightMm: number, font: TextFontStyle): number {
  if (heightMm <= 0) {
    return 1;
  }

  const lineUnit = font.heightMm + font.lineSpacingMm;
  if (lineUnit <= 0) {
    return 1;
  }

  // N lines require N*height + (N-1)*spacing <= heightMm.
  return Math.max(1, Math.floor((heightMm + font.lineSpacingMm) / lineUnit));
}

export function resolveTextLayout({
  element,
  value,
  widthMm,
  heightMm,
  maxScale,
}: TextLayoutRequest): ResolvedTextLayout {
  const strategy = element.font.overflowStrategy;
  const minScale = Math.min(
    1,
    element.font.minHeightMm / element.font.heightMm,
  );
  const step = 0.05;
  const isCharFill = element.fitMode === "char-fill";
  let scale = Math.max(minScale, Math.min(1, maxScale));
  const maxIterations = Math.ceil((1 - minScale) / step) + 2;

  for (let iteration = 0; iteration < maxIterations; iteration++) {
    const scaledFont = buildScaledFont(element.font, scale);
    const measure = createCharMeasure(scaledFont.family, scaledFont.widthMm);
    const effectiveMaxLines =
      heightMm === undefined
        ? scaledFont.maxLines
        : Math.min(scaledFont.maxLines, maxLinesByHeight(heightMm, scaledFont));

    const wrapped = wrapTextWithExplicitBreaks(value, widthMm, measure);
    const charFilled = isCharFill
      ? charFillLines(value, widthMm, effectiveMaxLines, measure)
      : { lines: [] as string[], overflowed: false };
    const lines = isCharFill ? charFilled.lines : wrapped;
    const doesFit = isCharFill
      ? !charFilled.overflowed
      : wrapped.length <= effectiveMaxLines;

    if (doesFit) {
      const lineCount = Math.max(1, lines.length);
      const diagnostic =
        scale < 0.999
          ? {
              elementId: element.id,
              adjustment: "shrink" as const,
              detail: `Scaled font to ${(scale * 100).toFixed(0)}% to fit content.`,
            }
          : undefined;

      return {
        lines: lines.length === 0 ? [""] : lines,
        lineCount,
        font: scaledFont,
        contentHeightMm: heightFromLines(lineCount, scaledFont),
        diagnostic,
        overflowed: false,
        ellipsized: false,
      };
    }

    if (strategy === "shrink" && scale > minScale + 0.0001) {
      const nextScale = Math.max(minScale, scale - step);
      if (nextScale !== scale) {
        scale = nextScale;
        continue;
      }
    }

    if (strategy === "ellipsis" || strategy === "shrink") {
      const ellipsized = isCharFill
        ? charFillWithEllipsis(value, widthMm, effectiveMaxLines, measure)
        : ellipsizeLines(wrapped, effectiveMaxLines, widthMm, measure);
      const lineCount = Math.max(1, ellipsized.length);
      return {
        lines: ellipsized,
        lineCount,
        font: scaledFont,
        contentHeightMm: heightFromLines(lineCount, scaledFont),
        diagnostic: {
          elementId: element.id,
          adjustment: "ellipsis",
          detail: "Applied ellipsis to keep text within max lines.",
        },
        overflowed: true,
        ellipsized: true,
      };
    }

    const clippedLines = isCharFill
      ? charFilled.lines
      : wrapped.slice(0, Math.max(1, effectiveMaxLines));
    const lineCount = Math.max(1, clippedLines.length);

    return {
      lines: clippedLines,
      lineCount,
      font: scaledFont,
      contentHeightMm: heightFromLines(lineCount, scaledFont),
      diagnostic: {
        elementId: element.id,
        adjustment: "clip",
        detail: "Applied hard clipping to keep text within max lines.",
      },
      overflowed: true,
      ellipsized: false,
    };
  }

  // Fallback: clip at current scale (unreachable in normal operation).
  const fallbackFont = buildScaledFont(element.font, scale);
  return {
    lines: [""],
    lineCount: 1,
    font: fallbackFont,
    contentHeightMm: heightFromLines(1, fallbackFont),
    diagnostic: {
      elementId: element.id,
      adjustment: "clip",
      detail: "Applied hard clipping to keep text within max lines.",
    },
    overflowed: true,
    ellipsized: false,
  };
}
